import React from 'react';

import { PlusOutlined } from '@ant-design/icons';
import { Alert, Button, Flex, Result, Table, Typography } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { useTranslation } from 'react-i18next';

import { NSKeys } from '@app/i18n';
import { Module, ParticipationRowStatus, RetrieveParticipationSchema } from '@app/models/users';
import { isForbiddenError } from '@app/services/apiErrors';
import { formatDate, formatDateAndTime, fromUtc } from '@app/services/date';
import { formatContract } from '@app/services/formatContract';
import { useApiErrorMessage } from '@app/services/hooks/useApiErrorMessage';
import { useLocalSelector } from '@app/services/hooks/useRedux';
import { RAW_INTERPOLATION } from '@app/services/i18n';
import { stringToBoolean } from '@app/services/stringToBoolean';
import { AccessDenied } from '@components/AccessDenied';

import { useCoalitionQuery } from '../../hooks';
import { InvitationStatusTag } from '../InvitationStatusTag';
import { ParticipationStatusTag } from '../ParticipationStatusTag';

import { AddParticipantModal } from './components/AddParticipantModal';
import { InviteModal } from './components/InviteModal';
import { ParticipationActions } from './components/ParticipationActions';
import { PoasModal } from './components/PoasModal';

export const CoalitionContainer: React.FC = () => {
  const scope = useLocalSelector((state) => state.currentSession.data?.scope);
  const spaceT = useTranslation(NSKeys.spaceSettings);
  const commonT = useTranslation(NSKeys.common);
  const getErrorMessage = useApiErrorMessage();
  const coalition = useCoalitionQuery();
  const [addOpen, setAddOpen] = React.useState(false);
  const [invited, setInvited] = React.useState<RetrieveParticipationSchema>();
  const [poasOf, setPoasOf] = React.useState<RetrieveParticipationSchema>();
  const isSandbox = !!scope?.is_sandbox;
  const canManage = !!scope?.$permissions?.manage_coalition;

  if (isForbiddenError(coalition.error)) {
    return <AccessDenied withProfileLink={false} />;
  }
  if (coalition.isError) {
    return (
      <Result
        status="error"
        subTitle={getErrorMessage(coalition.error)}
        extra={<Button onClick={() => coalition.refetch()}>{commonT.t('buttonReload')}</Button>}
      />
    );
  }

  const deliveryScenario = coalition.data?.module_settings.find(
    (settings) => settings.module === Module.Delivery
  )?.scenario;

  const columns: ColumnsType<RetrieveParticipationSchema> = [
    {
      title: spaceT.t('coalition.columns.counterparty'),
      key: 'legal_entity',
      render: (_, row) => (
        <Flex vertical>
          <Typography.Text>{row.legal_entity.name}</Typography.Text>
          <Typography.Text type="secondary" style={{ fontSize: 12 }}>
            {commonT.t('inn')} {row.legal_entity.inn}
          </Typography.Text>
        </Flex>
      ),
    },
    {
      title: spaceT.t('coalition.columns.module'),
      dataIndex: 'module',
      key: 'module',
      render: (value: Module) => commonT.t(`module.${value}` as const),
    },
    {
      title: spaceT.t('coalition.columns.scenario'),
      key: 'scenario',
      render: (_, row) =>
        row.module === Module.Delivery && deliveryScenario ? spaceT.t(`scenario.${deliveryScenario}` as const) : '—',
    },
    {
      title: spaceT.t('coalition.columns.contract'),
      dataIndex: 'contract_number',
      key: 'contract_number',
      render: (_, row) => formatContract(row.contract_number, row.contract_date),
    },
    {
      title: spaceT.t('coalition.columns.status'),
      key: 'status',
      render: (_, row) => {
        const since = row.status === ParticipationRowStatus.Deactivated ? row.deactivated_at : row.activated_at;
        return (
          <Flex vertical align="flex-start" gap={4}>
            <ParticipationStatusTag status={row.status} />
            {since && [ParticipationRowStatus.Active, ParticipationRowStatus.Deactivated].includes(row.status) && (
              <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                {spaceT.t('coalition.since', { date: formatDate(fromUtc(since)), ...RAW_INTERPOLATION })}
              </Typography.Text>
            )}
          </Flex>
        );
      },
    },
    {
      title: spaceT.t('coalition.columns.invitation'),
      key: 'last_invitation',
      render: (_, row) =>
        row.last_invitation ? (
          <Flex vertical align="flex-start" gap={4}>
            <Typography.Text>{row.last_invitation.email}</Typography.Text>
            <Flex gap={4} align="center" wrap>
              <InvitationStatusTag status={row.last_invitation.status} />
              <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                {spaceT.t('coalition.until', {
                  date: formatDateAndTime(fromUtc(row.last_invitation.expires_at)),
                  ...RAW_INTERPOLATION,
                })}
              </Typography.Text>
            </Flex>
          </Flex>
        ) : (
          '—'
        ),
    },
    {
      title: '',
      key: 'actions',
      render: (_, row) => (
        <ParticipationActions
          participation={row}
          disabled={isSandbox}
          onInvite={setInvited}
          onPoas={stringToBoolean(process.env.REACT_APP_FEATURE_EPD) ? setPoasOf : undefined}
        />
      ),
    },
  ];

  return (
    <Flex vertical gap={16}>
      {isSandbox && (
        <Alert
          type="warning"
          showIcon
          title={spaceT.t('coalition.sandboxTitle')}
          description={spaceT.t('coalition.sandboxDescription')}
        />
      )}
      {canManage && (
        <Flex justify="flex-end">
          <Button type="primary" icon={<PlusOutlined />} disabled={isSandbox} onClick={() => setAddOpen(true)}>
            {spaceT.t('coalition.add')}
          </Button>
        </Flex>
      )}
      <Table<RetrieveParticipationSchema>
        rowKey="uid"
        className="tableClassname"
        loading={coalition.isLoading}
        dataSource={coalition.data?.participations}
        columns={columns}
        pagination={false}
        scroll={{ x: true }}
        locale={{ emptyText: spaceT.t('coalition.empty') }}
      />
      <AddParticipantModal open={addOpen} onClose={() => setAddOpen(false)} />
      <InviteModal participation={invited} onClose={() => setInvited(undefined)} />
      <PoasModal participation={poasOf} onClose={() => setPoasOf(undefined)} />
    </Flex>
  );
};
